import jspdfDist from 'jspdf/dist/jspdf.umd.min.js';
import { formatPhoneNumber } from '../../phoneUtils.js';

const jsPDF = jspdfDist.jsPDF || (jspdfDist.default && jspdfDist.default.jsPDF) || jspdfDist;

/**
 * Formats a number as AUD currency.
 */
function formatCurrency(val) {
  const num = Number(val) || 0;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
}

/**
 * Formats date string into readable Australian standard (e.g. 24 Sep 2026).
 */
function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
}

/**
 * Generates a clean, professional, commercial PDF quotation for the client.
 * NOTE: Strictly DOES NOT contain any TruckTrack branding, logos, or wording.
 * 
 * @param {Object} quote Customer quote object
 * @param {Object} options Calculation totals and itemized packages
 */
export function generateQuotePdf(quote, options = {}) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  const pageWidth = doc.internal.pageSize.getWidth(); // 210mm
  const pageHeight = doc.internal.pageSize.getHeight(); // 297mm
  const marginX = 14;
  const contentWidth = pageWidth - (marginX * 2); // 182mm
  const rightMarginX = pageWidth - marginX;

  // Modern clean palette (Navy / Slate / Neutral)
  const colors = {
    primary: [15, 23, 42],        // #0f172a Deep Slate / Navy
    primaryLight: [30, 41, 59],   // #1e293b Slate 800
    accent: [16, 185, 129],       // #10b981 Emerald
    accentBg: [240, 253, 244],    // #f0fdf4 Light Emerald
    textDark: [15, 23, 42],       // #0f172a
    textMedium: [71, 85, 105],    // #475569
    textMuted: [100, 116, 139],   // #64748b
    cardBg: [248, 250, 252],      // #f8fafc Light Slate
    cardBorder: [226, 232, 240],  // #e2e8f0
    borderLight: [241, 245, 249], // #f1f5f9
    white: [255, 255, 255]
  };

  let y = 14;

  // ========================================================
  // 1. TOP HEADER SECTION
  // ========================================================
  // Left: Document Title & Metadata
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...colors.primary);
  doc.text('QUOTATION', marginX, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...colors.textMuted);
  doc.text('Commercial Cartage & Materials Disposal Proposal', marginX, y + 12);

  // Right: Quote Reference Block
  const quoteNum = quote.quoteNumber || (quote.id ? `QT-${quote.id}` : 'QT-DRAFT');
  const quoteDateFormatted = formatDate(quote.quoteDate || new Date().toISOString());
  const validUntilFormatted = formatDate(quote.validUntil || new Date(Date.now() + 30 * 86400000).toISOString());

  // Quote metadata box on right
  const metaBoxWidth = 72;
  const metaBoxX = rightMarginX - metaBoxWidth;
  doc.setFillColor(...colors.cardBg);
  doc.setDrawColor(...colors.cardBorder);
  doc.setLineWidth(0.3);
  doc.roundedRect(metaBoxX, y - 2, metaBoxWidth, 24, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...colors.textMuted);
  doc.text('QUOTE NO:', metaBoxX + 4, y + 3);
  doc.text('DATE:', metaBoxX + 4, y + 9);
  doc.text('VALID UNTIL:', metaBoxX + 4, y + 15);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...colors.primary);
  doc.text(quoteNum, metaBoxX + 28, y + 3);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.textDark);
  doc.text(quoteDateFormatted, metaBoxX + 28, y + 9);
  doc.text(validUntilFormatted, metaBoxX + 28, y + 15);

  // Official Status badge
  const statusText = (quote.status === 'accepted' || quote.status === 'converted') ? 'CONFIRMED' : 'OFFICIAL QUOTE';
  doc.setFillColor(...colors.accentBg);
  doc.setDrawColor(...colors.accent);
  doc.setLineWidth(0.2);
  doc.roundedRect(metaBoxX + 4, y + 17, metaBoxWidth - 8, 4.5, 1, 1, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(4, 120, 87);
  doc.text(statusText, metaBoxX + (metaBoxWidth / 2), y + 20.3, { align: 'center' });

  y += 28;

  // Decorative Accent Divider
  doc.setFillColor(...colors.primaryLight);
  doc.rect(marginX, y, contentWidth - 40, 1.2, 'F');
  doc.setFillColor(...colors.accent);
  doc.rect(marginX + contentWidth - 40, y, 40, 1.2, 'F');

  y += 6;

  // ========================================================
  // 2. CLIENT & PROJECT DETAILS (TWO CARDS)
  // ========================================================
  const cardGap = 5;
  const colWidth = (contentWidth - cardGap) / 2;
  const cardHeight = 36;

  // Left Card: CLIENT DETAILS
  doc.setFillColor(...colors.cardBg);
  doc.setDrawColor(...colors.cardBorder);
  doc.setLineWidth(0.3);
  doc.roundedRect(marginX, y, colWidth, cardHeight, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...colors.textMuted);
  doc.text('CLIENT DETAILS', marginX + 5, y + 6);

  const clientName = (quote.customerName && quote.customerName.trim()) ? quote.customerName.trim() : 'Valued Client';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...colors.primary);
  doc.text(doc.splitTextToSize(clientName, colWidth - 10), marginX + 5, y + 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...colors.textMedium);
  let clientY = y + 17;
  const attnName = quote.prepared_for || quote.contactPerson;
  if (attnName) {
    doc.text(`Attn: ${attnName}`, marginX + 5, clientY);
    clientY += 4.5;
  }
  if (quote.phone) {
    doc.text(`Ph: ${formatPhoneNumber(quote.phone)}`, marginX + 5, clientY);
    clientY += 4.5;
  }
  if (quote.email) {
    doc.text(`Email: ${quote.email}`, marginX + 5, clientY);
  }

  // Right Card: SITE & PROJECT LOGISTICS
  const rightCardX = marginX + colWidth + cardGap;
  doc.setFillColor(...colors.cardBg);
  doc.setDrawColor(...colors.cardBorder);
  doc.roundedRect(rightCardX, y, colWidth, cardHeight, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...colors.textMuted);
  doc.text('PROJECT & SITE LOCATION', rightCardX + 5, y + 6);

  const siteAddress = (quote.haulagePackages?.[0]?.siteAddress || quote.haulagePackages?.[0]?.load_address?.street || quote.haulagePackage?.siteAddress || quote.haulagePackage?.load_address?.street || quote.siteAddress || '').trim() || 'Site Address to be confirmed';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...colors.primary);
  const siteLines = doc.splitTextToSize(siteAddress, colWidth - 10);
  doc.text(siteLines.slice(0, 2), rightCardX + 5, y + 11.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...colors.textMedium);
  const hoursText = options.operatingHours || 'Standard Operating Hours: 07:00 – 17:00';
  doc.text(hoursText, rightCardX + 5, y + 23);
  doc.text('Fleet: Heavy Combination (Truck & Dog, 38t Payload)', rightCardX + 5, y + 28);

  y += cardHeight + 7;

  // ========================================================
  // 3. ITEMIZED QUOTE TABLE
  // ========================================================
  // Section Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...colors.primary);
  doc.text('SCHEDULE OF ITEMS & SERVICES', marginX, y);

  y += 3;

  // Table Column Definitions
  const colDef = [
    { title: 'Item / Material Description', width: 68, align: 'left' },
    { title: 'Disposal Destination', width: 44, align: 'left' },
    { title: 'Qty (Tonnes)', width: 22, align: 'right' },
    { title: 'Rate ($/t ex)', width: 22, align: 'right' },
    { title: 'Total (Ex GST)', width: 26, align: 'right' }
  ];

  // Table Header Row
  const headerHeight = 7;
  doc.setFillColor(...colors.primaryLight);
  doc.roundedRect(marginX, y, contentWidth, headerHeight, 1.5, 1.5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...colors.white);

  let curX = marginX;
  colDef.forEach(col => {
    const textX = col.align === 'right' ? curX + col.width - 3 : curX + 3;
    doc.text(col.title, textX, y + 4.8, { align: col.align });
    curX += col.width;
  });

  y += headerHeight;

  // Packages Items
  const packagesList = options.items && options.items.length > 0
    ? options.items
    : [{
        material: "General Haulage & Disposal",
        yardName: "Licensed Recycling Facility",
        suburb: "NSW",
        tonnes: 1,
        subtotal: options.combinedSubtotal || 0,
        unitRateExGst: options.combinedSubtotal || 0,
        allocatedTrucks: 1,
        calculatedDays: 1,
        loads: 1
      }];

  packagesList.forEach((pkg, index) => {
    const isAlt = index % 2 === 1;
    const rowHeight = 15;

    // Check page overflow
    if (y + rowHeight > pageHeight - 55) {
      doc.addPage();
      y = 16;
    }

    if (isAlt) {
      doc.setFillColor(...colors.cardBg);
      doc.rect(marginX, y, contentWidth, rowHeight, 'F');
    }

    doc.setDrawColor(...colors.cardBorder);
    doc.setLineWidth(0.2);
    doc.line(marginX, y + rowHeight, rightMarginX, y + rowHeight);

    let x = marginX;

    // Col 1: Material Description & Logistics Subtext
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...colors.primary);
    doc.text(pkg.material || 'Material Item', x + 3, y + 5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...colors.textMuted);
    const logText = `Allocated: ${pkg.allocatedTrucks || 1} Truck(s) | Est. Duration: ${pkg.calculatedDays || 1} Days (${pkg.loads || 1} loads)`;
    doc.text(logText, x + 3, y + 9.5);
    x += colDef[0].width;

    // Col 2: Destination Facility
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...colors.textDark);
    const destName = pkg.yardName || 'Licensed Disposal Yard';
    const destSuburb = pkg.suburb ? `(${pkg.suburb})` : '';
    doc.text(`${destName}`, x + 3, y + 5);
    if (destSuburb) {
      doc.setFontSize(7.5);
      doc.setTextColor(...colors.textMuted);
      doc.text(destSuburb, x + 3, y + 9.5);
    }
    x += colDef[1].width;

    // Col 3: Quantity (Tonnes)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...colors.textDark);
    const tonnesFormatted = Number(pkg.tonnes || 0).toLocaleString('en-AU', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' t';
    doc.text(tonnesFormatted, x + colDef[2].width - 3, y + 6.5, { align: 'right' });
    x += colDef[2].width;

    // Col 4: Unit Rate ($/tonne ex GST)
    const unitRateEx = (pkg.tonnes > 0 && pkg.subtotal > 0) ? (pkg.subtotal / pkg.tonnes) : Number(pkg.unitRateExGst || 0);
    doc.text(formatCurrency(unitRateEx) + '/t', x + colDef[3].width - 3, y + 6.5, { align: 'right' });
    x += colDef[3].width;

    // Col 5: Total (Ex GST)
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text(formatCurrency(pkg.subtotal || 0), x + colDef[4].width - 3, y + 6.5, { align: 'right' });

    y += rowHeight;
  });

  y += 4;

  // ========================================================
  // 4. TOTALS & SUMMARY BLOCK
  // ========================================================
  const summaryBoxWidth = 86;
  const summaryBoxX = rightMarginX - summaryBoxWidth;
  const summaryBoxHeight = 32;

  // Left Note or Logistics Summary alongside Totals
  const leftNoteWidth = contentWidth - summaryBoxWidth - 6;
  doc.setFillColor(...colors.cardBg);
  doc.setDrawColor(...colors.cardBorder);
  doc.setLineWidth(0.3);
  doc.roundedRect(marginX, y, leftNoteWidth, summaryBoxHeight, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...colors.textMuted);
  doc.text('LOGISTICS & HAULAGE SUMMARY', marginX + 4, y + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...colors.textMedium);
  const totalTonnesText = `Total Scope: ${Number(options.combinedTonnes || 0).toLocaleString()} Tonnes`;
  doc.text(totalTonnesText, marginX + 4, y + 11);
  doc.text('Tolls, levies, cartage & tip fees included in rate', marginX + 4, y + 16);
  if (options.combinedAverageRate > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text(`Effective Client Rate: ${formatCurrency(options.combinedAverageRate)} / tonne (inc GST)`, marginX + 4, y + 23);
  }

  // Right Totals Box
  doc.setFillColor(...colors.cardBg);
  doc.setDrawColor(...colors.cardBorder);
  doc.roundedRect(summaryBoxX, y, summaryBoxWidth, summaryBoxHeight, 2, 2, 'FD');

  let sumY = y + 5.5;
  const labelX = summaryBoxX + 5;
  const valX = summaryBoxX + summaryBoxWidth - 5;

  // Subtotal (ex GST)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...colors.textMedium);
  doc.text('Subtotal (Excl. GST):', labelX, sumY);
  doc.text(formatCurrency(options.combinedSubtotal || 0), valX, sumY, { align: 'right' });
  sumY += 5.5;

  // GST (10%)
  doc.text('GST (10%):', labelX, sumY);
  doc.text(formatCurrency(options.combinedGst || 0), valX, sumY, { align: 'right' });
  sumY += 5;

  // Divider
  doc.setDrawColor(...colors.cardBorder);
  doc.setLineWidth(0.4);
  doc.line(labelX, sumY, valX, sumY);
  sumY += 5.5;

  // TOTAL (INCL. GST)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...colors.primary);
  doc.text('TOTAL (INCL. GST):', labelX, sumY);
  doc.text(formatCurrency(options.combinedGrandTotal || 0), valX, sumY, { align: 'right' });

  y += summaryBoxHeight + 7;

  // ========================================================
  // 5. COMMERCIAL TERMS & SPECIAL CONDITIONS
  // ========================================================
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...colors.primary);
  doc.text('TERMS & CONDITIONS', marginX, y);

  y += 3.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.2);
  doc.setTextColor(...colors.textMedium);

  const standardTerms = [
    `1. Payment Terms: ${quote.paymentTerms || quote.payment_terms || 'Strictly 14 days from date of tax invoice unless an approved credit facility is active.'}`,
    '2. Material Compliance: Quoted disposal rates strictly apply to clean, non-hazardous, uncontaminated material matching specification. Any contamination will be redirected or incur tipping surcharges.',
    '3. Site Access & Loading: Site to provide safe, all-weather unhindered access and loading facilities. Standby demurrage charges apply if loading exceeds standard cycle times.',
    '4. Tolls & Levies: All government road tolls and statutory EPA waste levy contributions in effect at quote date are fully included.',
    '5. Validity: This quotation is valid for 30 days from date of issue and is subject to fleet availability at time of formal order placement.'
  ];

  standardTerms.forEach(term => {
    doc.text(term, marginX, y);
    y += 3.5;
  });

  // User Notes
  if (quote.notes && quote.notes.trim()) {
    y += 1;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...colors.primary);
    doc.text('Special Notes & Instructions:', marginX, y);
    y += 3.5;

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.2);
    doc.setTextColor(...colors.textDark);
    const noteLines = doc.splitTextToSize(quote.notes.trim(), contentWidth);
    doc.text(noteLines.slice(0, 3), marginX, y);
    y += (noteLines.slice(0, 3).length * 3.5);
  }

  y += 3;

  // ========================================================
  // 6. CLIENT ACCEPTANCE / SIGN-OFF BLOCK
  // ========================================================
  // Check page overflow for sign-off block
  if (y > pageHeight - 38) {
    doc.addPage();
    y = 16;
  }

  const signBoxHeight = 22;
  doc.setFillColor(...colors.cardBg);
  doc.setDrawColor(...colors.cardBorder);
  doc.setLineWidth(0.3);
  doc.roundedRect(marginX, y, contentWidth, signBoxHeight, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...colors.primary);
  doc.text('CLIENT ACCEPTANCE CONFIRMATION', marginX + 5, y + 4.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...colors.textMuted);
  doc.text('To accept this quotation and authorize scheduling, please sign and return this document:', marginX + 5, y + 8.5);

  const signColWidth = (contentWidth - 20) / 3;
  let signX = marginX + 5;
  const lineY = y + 17;

  // Signature line 1: Authorized Signature
  doc.setDrawColor(180, 190, 205);
  doc.setLineWidth(0.3);
  doc.line(signX, lineY, signX + signColWidth, lineY);
  doc.text('Authorized Signature', signX, lineY + 3.5);

  // Signature line 2: Printed Name & Title
  signX += signColWidth + 5;
  doc.line(signX, lineY, signX + signColWidth, lineY);
  doc.text('Printed Name & Title', signX, lineY + 3.5);

  // Signature line 3: Date
  signX += signColWidth + 5;
  doc.line(signX, lineY, signX + signColWidth, lineY);
  doc.text('Date', signX, lineY + 3.5);

  // ========================================================
  // 7. FOOTER (PAGE NUMBER & DATE - ZERO TRUCKTRACK WORDING)
  // ========================================================
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...colors.textMuted);

    // Bottom subtle line
    doc.setDrawColor(...colors.cardBorder);
    doc.setLineWidth(0.2);
    doc.line(marginX, pageHeight - 9, rightMarginX, pageHeight - 9);

    doc.text('Commercial In Confidence — Official Quotation', marginX, pageHeight - 5.5);
    doc.text(`Page ${i} of ${pageCount}`, rightMarginX, pageHeight - 5.5, { align: 'right' });
  }

  // Download Trigger
  const safeClientName = (quote.customerName || 'Client').replace(/[^a-zA-Z0-9_-]/g, '_');
  const safeQuoteNum = (quoteNum || 'QT').replace(/[^a-zA-Z0-9_-]/g, '_');
  const filename = `Quote_${safeQuoteNum}_${safeClientName}.pdf`;

  doc.save(filename);
  return filename;
}
